import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./ContactsList.module.css";
import {
  ContactModal,
  SyncDialog,
  UploadDialog,
} from "../ContactManager/ContactManager";
import {
  createContact,
  uploadContacts,
  syncNativeContacts,
  getGoogleSyncStatus,
  triggerGoogleContactsSync,
  isSyncContactSkip,
  listAgentContacts,
  setContactOwnerId,
} from "../../Store/apiStore";
import { markSetupStep } from "../../lib/setupScore";
import { trackEvent } from "../../lib/analytics";
import useTeamPermissions from "../../hooks/useTeamPermissions";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const authToken = () => localStorage.getItem("token") || "";

// Agent contact ko My Contacts wali shape me laate hain — row, search aur call icon ek hi code se chalein.
const asContactRow = (ac) => ({
  id: `ac-${ac.id}`,
  name: ac.contactName || ac.callerName || "",
  phone: ac.phone,
  company: ac.contactCompany || "",
  isVip: !!ac.isVip,
  fromAgent: true,
});

const initials = (name, phone) => {
  const src = (name || "").trim();
  if (!src) return (phone || "#").replace(/\D/g, "").slice(-2) || "#";
  return src
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
};

/*
 * Contacts tab ///of the dialer.
 *
 * The + menu (Sync / Add / Upload) drives the SAME sheets the contacts module
 * uses — SyncDialog, ContactModal and UploadDialog are imported straight from
 * ContactManager — and the handlers below mirror ContactsPage's, so a sync
 * started here behaves exactly like one started on the Contacts page.
 *
 * pendingGoogleSync / onGoogleSyncHandled
 *   Google OAuth leaves the app and comes back with ?google_sync=success.
 *   Dialer detects that and flips this flag; the sync itself runs here.
 */
const ContactsList = ({
  onPick,
  agentId,
  pendingGoogleSync = false,
  onGoogleSyncHandled,
}) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  // "all" | "mine" | "agent"
  const [tab, setTab] = useState("all");
  const [agentRows, setAgentRows] = useState([]);
  const [agentLoading, setAgentLoading] = useState(true);
  const [agentError, setAgentError] = useState(null);
  const [fabOpen, setFabOpen] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  // null | "syncing" | "done" | "done:<detail>" | "error" | "auth_needed"
  const [syncStatus, setSyncStatus] = useState(null);

  // Owner aur Admin team member ko dialer contacts me '+' button (Sync/Add/Upload) ka access hai.
  // Support team members ko '+' button ka access nahi hai (view-only).
  const { isTeamMember, isAdmin, role } = useTeamPermissions();
  const canManage = !isTeamMember || isAdmin || role === "admin";

  // Contacts per-owner hain — team member ke liye backend ko owner batana padta hai.
  const ownerId =
    sessionStorage.getItem("SelectAgentOwnerId") ||
    localStorage.getItem("teamOwnerId") ||
    null;

  // Axios wale calls (listAgentContacts) isi se X-Owner-Id uthate hain.
  useEffect(() => {
    setContactOwnerId(ownerId);
    return () => setContactOwnerId(null);
  }, [ownerId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (ownerId) params.set("ownerId", ownerId);
      const res = await fetch(`${API_BASE}/contact-routes/contacts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${authToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Owner ne contacts permission nahi di — backend ka message zyada saaf hota hai.
        throw new Error(
          data?.error ||
            (res.status === 403
              ? "You don’t have access to contacts on this account."
              : "Could not load contacts"),
        );
      }
      setContacts(data.contacts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  // Sirf isi agent ke — dialer hamesha ek agent ke context me khulta hai.
  const loadAgentContacts = useCallback(async () => {
    setAgentLoading(true);
    setAgentError(null);
    try {
      const data = await listAgentContacts({ agentId, limit: 500 });
      setAgentRows((data?.agentContacts || []).map(asContactRow));
    } catch (err) {
      setAgentError(
        err?.response?.data?.error || err?.message || "Could not load agent contacts",
      );
    } finally {
      setAgentLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadAgentContacts();
  }, [loadAgentContacts]);

  useEffect(() => {
    load();
  }, [load]);

  // Setup Score: contactsSynced — idempotent on the backend, fire and forget.
  const markSynced = useCallback(() => {
    try {
      if (agentId) markSetupStep("contactsSynced", agentId);
    } catch (_e) {
      /* never let score plumbing fail a sync */
    }
  }, [agentId]);

  const flashStatus = useCallback((status, ms = 4000) => {
    setSyncStatus(status);
    setTimeout(() => setSyncStatus((cur) => (cur === status ? null : cur)), ms);
  }, []);

  const syncedParts = (res) => {
    const parts = [];
    if (res?.inserted > 0) parts.push(`${res.inserted} new`);
    if (res?.updated > 0) parts.push(`${res.updated} duplicate (updated)`);
    if (res?.failed > 0) parts.push(`${res.failed} failed`);
    return parts;
  };

  // ── Google sync trigger (OAuth return OR from SyncDialog) ─────────────────
  const handleGoogleSyncTrigger = useCallback(async () => {
    setSyncStatus("syncing");
    try {
      const result = await triggerGoogleContactsSync();
      try {
        await isSyncContactSkip();
      } catch (_e) {
        /* skip-flag failure sync ko fail nahi karta */
      }
      markSynced();
      trackEvent("spam_filtering_enabled", {
        contacts_synced_count: (result?.inserted || 0) + (result?.updated || 0),
      });
      // Already-authorised Google → direct sync with no OAuth return, which was
      // never tracked (see ContactsPage.handleGoogleSyncTrigger). Success path
      // only; after an OAuth return Dialer.js has already fired and the
      // once-per-user guard drops this repeat.
      trackEvent("google_contacts_connected", { source: "dialer" });
      await load();
      const parts = syncedParts(result);
      flashStatus(
        parts.length ? `done:Google — ${parts.join(", ")}` : "done",
        6000,
      );
    } catch (err) {
      // needsAuth → stored token expired, user has to reconnect Google.
      if (err?.response?.data?.needsAuth) setSyncStatus("auth_needed");
      else flashStatus("error");
      throw err; // SyncDialog shows its own inline error too
    }
  }, [load, markSynced, flashStatus]);

  // Back from Google OAuth — finish the sync, then clear the flag so a
  // re-render doesn't fire it twice.
  useEffect(() => {
    if (!pendingGoogleSync) return;
    onGoogleSyncHandled?.();
    handleGoogleSyncTrigger().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGoogleSync]);

  // ── Native contacts pushed by React Native after permission ───────────────
  const handleNativeContactsReceived = useCallback(
    async (nativeContacts) => {
      setSyncStatus("syncing");
      try {
        const res = await syncNativeContacts(nativeContacts);
        markSynced();
        trackEvent("spam_filtering_enabled", {
          contacts_synced_count: (res?.inserted || 0) + (res?.updated || 0),
        });
        await load();
        const parts = syncedParts(res);
        flashStatus(parts.length ? `done:${parts.join(", ")}` : "done");
      } catch (_err) {
        flashStatus("error");
      }
    },
    [load, markSynced, flashStatus],
  );

  useEffect(() => {
    const handleNativeMessage = async (event) => {
      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type === "CONTACTS_DATA" && Array.isArray(data.contacts)) {
          await handleNativeContactsReceived(data.contacts);
        }
      } catch (_err) {
        /* RN se aaya koi aur message tha — ignore */
      }
    };

    // iOS posts on window, Android on document.
    window.addEventListener("message", handleNativeMessage);
    document.addEventListener("message", handleNativeMessage);
    return () => {
      window.removeEventListener("message", handleNativeMessage);
      document.removeEventListener("message", handleNativeMessage);
    };
  }, [handleNativeContactsReceived]);

  // ── Sync handler handed to SyncDialog / UploadDialog ──────────────────────
  // Same contract (and same branches) as ContactsPage.handleSync.
  const handleSync = useCallback(
    async (type, file) => {
      try {
        if (type === "google") {
          const isRN = !!window.ReactNativeWebView;
          const res = await getGoogleSyncStatus({
            isMobile: isRN,
            flow: "contacts",
            returnTo: "/dialer",
          });
          if (res.hasToken) {
            await handleGoogleSyncTrigger();
          } else if (res.authUrl) {
            if (isRN) {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({
                  type: "OPEN_GOOGLE_AUTH",
                  authUrl: res.authUrl,
                  flow: "contacts",
                }),
              );
            } else {
              window.location.href = res.authUrl;
            }
          }
          return;
        }

        if (type === "file" && file) {
          setSyncStatus("syncing");
          const formData = new FormData();
          formData.append("file", file);
          const res = await uploadContacts(formData);
          markSynced();
          await load();
          const parts = syncedParts(res);
          flashStatus(parts.length ? `done:${parts.join(", ")}` : "done", 6000);
          return res;
        }

        if (type === "phone_picker" && Array.isArray(file)) {
          setSyncStatus("syncing");
          try {
            const res = await syncNativeContacts(file);
            markSynced();
            await load();
            flashStatus("done", 2000);
            return res; // SyncDialog reports the inserted / failed counts
          } catch (err) {
            flashStatus("error");
            throw err; // re-throw so SyncDialog can show the error
          }
        }

        if (type === "native") {
          // Sirf native app me chalta hai; response message listener pe aata hai.
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ type: "REQUEST_CONTACTS" }),
            );
          }
          return;
        }
      } catch (err) {
        flashStatus("error");
        throw err;
      }
    },
    [handleGoogleSyncTrigger, load, markSynced, flashStatus],
  );

  // ── Add Contact ───────────────────────────────────────────────────────────
  const handleCreateContact = useCallback(
    async (form) => {
      try {
        const res = await createContact(form);
        // The contacts module resolves these with a confirm dialog; the dialer
        // reports it so the user can go handle it in Contacts.
        if (res?.isDuplicate || res?.existsInAgentContacts) {
          return { error: "This number is already in your contacts." };
        }
        if (res?.failed > 0 && res?.errors?.length > 0) {
          return { error: res.errors[0]?.reason || "Invalid phone number" };
        }
        markSynced();
        await load();
        flashStatus(`done:${form.name || form.phone} added`);
        return res;
      } catch (err) {
        return {
          error:
            err?.response?.data?.error ||
            err.message ||
            "Failed to save contact",
        };
      }
    },
    [load, markSynced, flashStatus],
  );

  // "all" me dedupe ki zaroorat nahi — API khud wo numbers chhod deta hai jo My Contacts me hain.
  const source = useMemo(() => {
    if (tab === "mine") return contacts;
    if (tab === "agent") return agentRows;
    return [...contacts, ...agentRows];
  }, [tab, contacts, agentRows]);

  // Search locally — the list is already in memory and this keeps typing snappy.
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const withPhone = source.filter((c) => c.phone);
    if (!q) return withPhone;
    return withPhone.filter((c) =>
      [c.name, c.phone, c.email, c.company]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [source, search]);

  const banner =
    syncStatus === "syncing" ? (
      <div className={`${styles.banner} ${styles.bannerInfo}`}>
        <span className={styles.bannerSpinner} />
        Syncing contacts…
      </div>
    ) : syncStatus?.startsWith("done") ? (
      <div className={`${styles.banner} ${styles.bannerDone}`}>
        {syncStatus.includes(":")
          ? syncStatus.split(":")[1]
          : "Contacts synced"}
      </div>
    ) : syncStatus === "error" ? (
      <div className={`${styles.banner} ${styles.bannerError}`}>
        Sync failed. Please try again.
      </div>
    ) : syncStatus === "auth_needed" ? (
      <div className={`${styles.banner} ${styles.bannerWarn}`}>
        <span>Google account not connected.</span>
        <button
          type="button"
          className={styles.bannerBtn}
          onClick={() => {
            setSyncStatus(null);
            setShowSync(true);
          }}
        >
          Connect
        </button>
      </div>
    ) : null;

  // Jo tab khula hai uska hi loading/error dekhna hai.
  const tabBusy = tab === "agent" ? agentLoading : tab === "mine" ? loading : loading || agentLoading;
  const tabError = tab === "agent" ? agentError : tab === "mine" ? error : error || agentError;
  const retryTab = tab === "agent" ? loadAgentContacts : tab === "mine" ? load : () => {
    load();
    loadAgentContacts();
  };

  let body;
  if (tabBusy) {
    body = (
      <div className={styles.center}>
        <span className={styles.spinner} />
      </div>
    );
  } else if (tabError) {
    body = (
      <div className={styles.center}>
        <p className={styles.muted}>{tabError}</p>
        <button type="button" className={styles.retry} onClick={retryTab}>
          Try again
        </button>
      </div>
    );
  } else if (!shown.length) {
    body = (
      <div className={styles.center}>
        <p className={styles.muted}>
          {source.length
            ? "No contact matches that search"
            : tab === "agent"
              ? "No one has called this agent yet"
              : "No contacts synced yet"}
        </p>
      </div>
    );
  } else {
    body = (
      <ul className={styles.list}>
        {shown.map((c) => (
          <li key={c.id}>
            {/* Number keypad me bharta hai — call user khud dabata hai. */}
            <button
              type="button"
              className={styles.row}
              onClick={() => onPick?.(c.phone)}
            >
              <span className={styles.avatar}>{initials(c.name, c.phone)}</span>
              <span className={styles.info}>
                <span className={styles.name}>{c.name || c.phone}</span>
                <span className={styles.meta}>
                  {c.name ? c.phone : ""}
                  {c.company ? `${c.name ? " · " : ""}${c.company}` : ""}
                </span>
              </span>
              {tab === "all" && c.fromAgent ? (
                <span className={styles.agentTag}>Agent</span>
              ) : null}
              {c.isVip ? <span className={styles.vip}>VIP</span> : null}
              <svg
                className={styles.callIcon}
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M6.6 10.8c1.44 2.83 3.77 5.15 6.6 6.6l2.2-2.2c.28-.28.68-.36 1.03-.25 1.13.37 2.35.57 3.6.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1C10.85 21 3 13.15 3 3.5 3 2.95 3.45 2.5 4 2.5H7.5c.55 0 1 .45 1 1 0 1.25.2 2.47.57 3.6.11.35.03.75-.25 1.03l-2.22 2.17Z"
                  fill="#6524eb"
                />
              </svg>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.tabs}>
        {[
          { key: "all", label: "All", count: contacts.length + agentRows.length },
          { key: "mine", label: "My Contacts", count: contacts.length },
          { key: "agent", label: "Agent Contacts", count: agentRows.length },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.tab} ${tab === t.key ? styles.tabActive : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            <span className={styles.tabCount}>{t.count}</span>
          </button>
        ))}
      </div>

      <div className={styles.searchWrap}>
        <svg
          className={styles.searchIcon}
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle cx="11" cy="11" r="6.5" stroke="#8a8a95" strokeWidth="1.8" />
          <path
            d="M16 16l4.5 4.5"
            stroke="#8a8a95"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
        <input
          className={styles.search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contacts"
          aria-label="Search contacts"
        />
      </div>

      {banner}
      {body}

      {/* ── FAB — same plus menu as the contacts module ── */}
      {/* Agent tab par sync/add/upload ka koi matlab nahi — wo list agent ki calls se banti hai. */}
      {canManage && tab !== "agent" ? (
        <>
          {fabOpen ? (
            <div
              className={styles.fabBackdrop}
              onClick={() => setFabOpen(false)}
            />
          ) : null}
          <div className={styles.fabWrap}>
            {fabOpen ? (
              <div className={styles.fabMenu}>
                <button
                  type="button"
                  className={styles.fabItem}
                  onClick={() => {
                    setFabOpen(false);
                    setShowSync(true);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <polyline
                      points="23 4 23 10 17 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Sync Contacts
                </button>

                <button
                  type="button"
                  className={styles.fabItem}
                  onClick={() => {
                    setFabOpen(false);
                    setShowAdd(true);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <line
                      x1="12"
                      y1="5"
                      x2="12"
                      y2="19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <line
                      x1="5"
                      y1="12"
                      x2="19"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Add Contact
                </button>

                <button
                  type="button"
                  className={styles.fabItem}
                  onClick={() => {
                    setFabOpen(false);
                    setShowUpload(true);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <polyline
                      points="17 8 12 3 7 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <line
                      x1="12"
                      y1="3"
                      x2="12"
                      y2="15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Upload File
                </button>
              </div>
            ) : null}

            <button
              type="button"
              className={`${styles.fab} ${fabOpen ? styles.fabRotated : ""}`}
              onClick={() => setFabOpen((p) => !p)}
              aria-label={fabOpen ? "Close contact actions" : "Contact actions"}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <line
                  x1="12"
                  y1="5"
                  x2="12"
                  y2="19"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <line
                  x1="5"
                  y1="12"
                  x2="19"
                  y2="12"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </>
      ) : null}

      <SyncDialog
        isOpen={showSync}
        onClose={() => setShowSync(false)}
        onSync={handleSync}
        onTriggerGoogleSync={handleGoogleSyncTrigger}
        onContactsSynced={load}
        /* Google OAuth ke baad /contacts nahi, wapas dialer pe hi laao —
           backend is path ko whitelist karta hai (ALLOWED_CONTACTS_RETURN). */
        oauthReturnTo="/dialer"
      />

      <ContactModal
        isOpen={showAdd}
        contact={null}
        onClose={() => setShowAdd(false)}
        onSave={handleCreateContact}
      />

      <UploadDialog
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        onSync={handleSync}
      />
    </div>
  );
};

export default ContactsList;
